import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import SmoothScroll from "./lib/SmoothScroll";
import { LanguageProvider } from "./lib/i18n";
import { SiteContentProvider } from "./lib/content";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCompanies from "./pages/AdminCompanies";
import AdminCompanyDetail from "./pages/AdminCompanyDetail";
import CompanyInviteAccept from "./pages/CompanyInviteAccept";
import ProvHome from "./pages/provocative/ProvHome";
import ProvStart from "./pages/provocative/ProvStart";
import ProvDashboard from "./pages/provocative/ProvDashboard";
import ProvAdmin from "./pages/provocative/ProvAdmin";
import Join from "./pages/Join";
import PT from "./pages/PT";
import PTAccount from "./pages/PTAccount";
import PtLinkBooking from "./pages/PtLinkBooking";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import MemberPortal from "./pages/MemberPortal";
import Welkom from "./pages/Welkom";
import Roster from "./pages/Roster";
import { Terms, Privacy } from "./pages/Legal";
import JohWorld from "./pages/johworld/JohWorld";
import { Login, Register, ForgotPassword, ResetPassword, SetPassword } from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import { AuthProvider } from "./lib/AuthContext";
import RequireAuth from "./components/RequireAuth";
import AuthCallback from "./pages/AuthCallback";
import ProfileLanguageSync from "./components/ProfileLanguageSync";
import JaceGuard from "./pages/jace/JaceGuard";
import JaceDashboard from "./pages/jace/JaceDashboard";
import JaceSessions from "./pages/jace/JaceSessions";
import JaceSessionView from "./pages/jace/JaceSessionView";
import JaceSessionBuilder from "./pages/jace/JaceSessionBuilder";
import JaceLibrary from "./pages/jace/JaceLibrary";
import JaceEngine from "./pages/jace/JaceEngine";
import JacePlay from "./pages/jace/JacePlay";
import JaceReview from "./pages/jace/JaceReview";
import JaceArchive from "./pages/jace/JaceArchive";
import JaceFeedback from "./pages/jace/JaceFeedback";
import JaceFeedbackEditor from "./pages/jace/JaceFeedbackEditor";
import JaceFeedbackSettings from "./pages/jace/JaceFeedbackSettings";
import JaceAthletes from "./pages/jace/JaceAthletes";
import JaceAthleteDetail from "./pages/jace/JaceAthleteDetail";
import JaceGroups from "./pages/jace/JaceGroups";
import P11Home from "./pages/project11/P11Home";
import P11Register from "./pages/project11/P11Register";
import P11Login from "./pages/project11/P11Login";
import P11Verify from "./pages/project11/P11Verify";
import P11Welcome from "./pages/project11/P11Welcome";
import P11Identity from "./pages/project11/P11Identity";
import P11Preparation from "./pages/project11/P11Preparation";
import P11Dashboard from "./pages/project11/P11Dashboard";
import P11Sessions from "./pages/project11/P11Sessions";
import P11Record from "./pages/project11/P11Record";
import P11Profile from "./pages/project11/P11Profile";
import P11Guard from "./pages/project11/P11Guard";
import P11AdminGuard from "./pages/project11/P11AdminGuard";
import P11AdminHome from "./pages/project11/P11AdminHome";
import P11AdminOverview from "./pages/project11/P11AdminOverview";
import P11AdminParticipants from "./pages/project11/P11AdminParticipants";
import P11AdminParticipantDetail from "./pages/project11/P11AdminParticipantDetail";
import P11AdminSessions from "./pages/project11/P11AdminSessions";
import P11AdminAttendance from "./pages/project11/P11AdminAttendance";
import P11AdminNumbers from "./pages/project11/P11AdminNumbers";
import P11AdminProject from "./pages/project11/P11AdminProject";
import { P11AdminNotifications, P11AdminSettings } from "./pages/project11/P11AdminMisc";

function Site() {
  return (
    <SmoothScroll>
      <Nav />
      <Home />
      <Footer />
    </SmoothScroll>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Google OAuth returns with #session_id=... in the fragment — process it
  // synchronously during render, before any route or auth guard can run.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Site />} />
      <Route path="/welkom" element={<Welkom />} />
      <Route path="/landingpage" element={<Welkom />} />
      <Route path="/rooster/:token" element={<Roster />} />
      <Route path="/voorwaarden" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/join" element={<Join />} />
      <Route path="/pt" element={<PT />} />
      <Route path="/pt/boek/:token" element={<PtLinkBooking />} />
      <Route path="/pt/dashboard" element={<PTAccount />} />
      <Route path="/mij" element={<MemberPortal />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/profiel" element={<Profile />} />
      <Route path="/login" element={<Login />} />
      <Route path="/registreren" element={<Register />} />
      <Route path="/wachtwoord-vergeten" element={<ForgotPassword />} />
      <Route path="/wachtwoord-reset" element={<ResetPassword />} />
      <Route path="/wachtwoord-instellen" element={<SetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
      {/* BEDRIJVEN — Company Ecosystem (Fase 01) */}
      <Route path="/admin/bedrijven" element={<AdminCompanies />} />
      <Route path="/admin/bedrijven/:id" element={<AdminCompanyDetail />} />
      <Route path="/invite/company/:token" element={<CompanyInviteAccept />} />
      {/* PROVOCATIVE. — canonieke lowercase route (component normaliseert elke case-variant) + redirects van oudere varianten */}
      <Route path="/provocative" element={<ProvHome />} />
      <Route path="/provocative/start" element={<ProvStart />} />
      <Route path="/provocative/dashboard" element={<ProvDashboard />} />
      <Route path="/provocative/admin" element={<ProvAdmin />} />
      <Route path="/Provokactive" element={<Navigate to="/provocative" replace />} />
      <Route path="/provokactive" element={<Navigate to="/provocative" replace />} />
      <Route path="/jace" element={<JaceGuard><Navigate to="/jace/dashboard" replace /></JaceGuard>} />
      <Route path="/jace/dashboard" element={<JaceGuard><JaceDashboard /></JaceGuard>} />
      <Route path="/jace/sessions" element={<JaceGuard><JaceSessions /></JaceGuard>} />
      <Route path="/jace/sessions/new" element={<JaceGuard><JaceSessionBuilder /></JaceGuard>} />
      <Route path="/jace/sessions/:sessionId" element={<JaceGuard><JaceSessionView /></JaceGuard>} />
      <Route path="/jace/sessions/:sessionId/edit" element={<JaceGuard><JaceSessionBuilder /></JaceGuard>} />
      <Route path="/jace/sessions/:sessionId/review" element={<JaceGuard><JaceReview /></JaceGuard>} />
      <Route path="/jace/play/engine/:itemId" element={<JaceGuard><JacePlay /></JaceGuard>} />
      <Route path="/jace/play/:sessionId" element={<JaceGuard><JacePlay /></JaceGuard>} />
      <Route path="/jace/library" element={<JaceGuard><JaceLibrary /></JaceGuard>} />
      <Route path="/jace/engine" element={<JaceGuard><JaceEngine /></JaceGuard>} />
      <Route path="/jace/archive" element={<JaceGuard><JaceArchive /></JaceGuard>} />
      <Route path="/jace/feedback" element={<JaceGuard><JaceFeedback /></JaceGuard>} />
      <Route path="/jace/feedback/settings" element={<JaceGuard><JaceFeedbackSettings /></JaceGuard>} />
      <Route path="/jace/feedback/new" element={<JaceGuard><JaceFeedbackEditor /></JaceGuard>} />
      <Route path="/jace/feedback/:feedbackId" element={<JaceGuard><JaceFeedbackEditor /></JaceGuard>} />
      <Route path="/jace/athletes" element={<JaceGuard><JaceAthletes /></JaceGuard>} />
      <Route path="/jace/athletes/:athleteId" element={<JaceGuard><JaceAthleteDetail /></JaceGuard>} />
      <Route path="/jace/groups" element={<JaceGuard><JaceGroups /></JaceGuard>} />
      {/* PROJECT 11* */}
      <Route path="/project11" element={<P11Home />} />
      <Route path="/project11/register" element={<P11Register />} />
      <Route path="/project11/login" element={<P11Login />} />
      <Route path="/project11/verify" element={<P11Verify />} />
      <Route path="/project11/welcome" element={<P11Guard><P11Welcome /></P11Guard>} />
      <Route path="/project11/identity" element={<P11Guard><P11Identity /></P11Guard>} />
      <Route path="/project11/preparation" element={<P11Guard><P11Preparation /></P11Guard>} />
      <Route path="/project11/dashboard" element={<P11Guard><P11Dashboard /></P11Guard>} />
      <Route path="/project11/sessions" element={<P11Guard><P11Sessions /></P11Guard>} />
      <Route path="/project11/record" element={<P11Guard><P11Record /></P11Guard>} />
      <Route path="/project11/profile" element={<P11Guard><P11Profile /></P11Guard>} />
      <Route path="/project11/admin" element={<P11AdminGuard><P11AdminHome /></P11AdminGuard>} />
      <Route path="/project11/admin/overview" element={<P11AdminGuard><P11AdminOverview /></P11AdminGuard>} />
      <Route path="/project11/admin/participants" element={<P11AdminGuard><P11AdminParticipants /></P11AdminGuard>} />
      <Route path="/project11/admin/participants/:participantId" element={<P11AdminGuard><P11AdminParticipantDetail /></P11AdminGuard>} />
      <Route path="/project11/admin/sessions" element={<P11AdminGuard><P11AdminSessions /></P11AdminGuard>} />
      <Route path="/project11/admin/sessions/:sessionId" element={<P11AdminGuard><P11AdminAttendance /></P11AdminGuard>} />
      <Route path="/project11/admin/numbers" element={<P11AdminGuard><P11AdminNumbers /></P11AdminGuard>} />
      <Route path="/project11/admin/project" element={<P11AdminGuard><P11AdminProject /></P11AdminGuard>} />
      <Route path="/project11/admin/notifications" element={<P11AdminGuard><P11AdminNotifications /></P11AdminGuard>} />
      <Route path="/project11/admin/settings" element={<P11AdminGuard><P11AdminSettings /></P11AdminGuard>} />
      <Route path="/johworld" element={<JohWorld />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#F5F3F0",
            border: "1px solid #1A1A1A",
            borderRadius: 0,
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
      <SiteContentProvider>
        <LanguageProvider>
          <AuthProvider>
            <ProfileLanguageSync />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </SiteContentProvider>
    </div>
  );
}
